import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import InternalHeader from 'components/Navigation/InternalHeader';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { REQUIRED_RULE } from 'utils/constants';
import { cleanObject } from 'utils/functions';
import { BUTTON_COLORS } from 'utils/enums';
import { useUbigeo } from 'hooks/useUbigeo';
import { countries } from 'utils/countries';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function ProfilePage() {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm();
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const isPep = watch('pep');
  const country = watch('country');
  const department = watch('department');
  const province = watch('province');

  const [departments, provinces, districts] = useUbigeo(
    country !== 'PE',
    generalApi,
    department,
    province
  );

  const [loading, setLoading] = useState(false);

  const submitAction = async (data: any) => {
    setLoading(true);
    let result: any = await generalApi.put('/edit_profile', {
      ...cleanObject(data),
      telephone: data.telephone !== '' ? data.telephone : undefined,
      pep: data.pep === 'Si',
    });
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Perfil actualizado');
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        watch={watch}
        control={control}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  useEffect(() => {
    const getProfile = async () => {
      const result = await generalApi.get('/profile');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      if (result.data) {
        Object.entries(result.data).forEach(([name, value]: any) => setValue(name, value));
        setValue('pep', result.data.pep ? 'Si' : 'No');
        if (result.data.pep) {
          setValue('pep_institute', result.data.pep.institute);
          setValue('pep_sice', new Date(result.data.pep.sice));
        }
      }
    };
    getProfile();
  }, [generalApi, setValue]);

  useEffect(() => {
    setValue('province', provinces[0]);
  }, [provinces, setValue]);

  useEffect(() => {
    setValue('district', districts[0]);
  }, [districts, setValue]);

  return (
    <PrivateRoute>
      <div className="min-h-full bg-gray-100 text-[#1B205A]">
        <InternalHeader />
        <div className="mx-auto max-w-5xl pb-8 pt-32 sm:pt-0 2xl:px-24">
          <div className="justify-between space-y-4 px-8 pt-8 pb-4 sm:flex sm:space-y-0 sm:px-0">
            <p className="text-2xl font-bold">Mi Perfil</p>
            <div className="space-y-2 sm:space-y-0 sm:space-x-2">
              <OutlineButton color={BUTTON_COLORS.BLUE}>Cambiar contraseña</OutlineButton>
              <OutlineButton
                color={BUTTON_COLORS.BLUE}
                onClick={() => history('/create_operation')}
              >
                Nuevas Operaciones
              </OutlineButton>
            </div>
          </div>
          <form onSubmit={handleSubmit(submitAction)} className="bg-white shadow sm:rounded-lg">
            <div className="divide-y divide-gray-200">
              <div className="grid grid-cols-2 gap-6 px-8 pt-8 pb-8">
                {buildBaseInput('Nombres', 'text', 'name', REQUIRED_RULE, {})}
                {buildBaseInput('Apellido Paterno', 'text', 'lname_p', REQUIRED_RULE, {})}
                {buildBaseInput('Apellido Materno', 'text', 'lname_m', REQUIRED_RULE, {})}
                {buildBaseInput(
                  'Nacionalidad',
                  'select',
                  'country',
                  {},
                  {
                    options: countries,
                  }
                )}
                {buildBaseInput('Dirección', 'text', 'address', REQUIRED_RULE, {})}
                {buildBaseInput(
                  'Región *',
                  country === 'PE' ? 'select' : 'text',
                  'department',
                  REQUIRED_RULE,
                  {
                    options: departments.map((obj) => ({
                      name: obj,
                    })),
                  }
                )}
                {buildBaseInput(
                  'Provincia *',
                  country === 'PE' ? 'select' : 'text',
                  'province',
                  REQUIRED_RULE,
                  {
                    options: provinces.map((obj) => ({
                      name: obj,
                    })),
                  }
                )}
                {buildBaseInput(
                  'Distrito *',
                  country === 'PE' ? 'select' : 'text',
                  'district',
                  REQUIRED_RULE,
                  {
                    options: districts.map((obj) => ({
                      name: obj,
                    })),
                  }
                )}
                {buildBaseInput('Ocupación', 'text', 'job', REQUIRED_RULE, {})}
                {buildBaseInput('Correo electrónico', 'email', 'email', REQUIRED_RULE, {
                  disabled: true,
                })}
                {buildBaseInput('Celular', 'text', 'cellphone', REQUIRED_RULE, {})}
                {buildBaseInput(
                  'Teléfono',
                  'text',
                  'telephone',
                  {},
                  {
                    helperText: '0 + Código de Ciudad + Número Telefónico',
                  }
                )}
                {buildBaseInput('PEP', 'select', 'pep', REQUIRED_RULE, {
                  helperText: 'Persona Expuesta Políticamente',
                  options: [
                    {
                      name: 'Si',
                    },
                    {
                      name: 'No',
                    },
                  ],
                })}

                {isPep === 'Si' ? (
                  buildBaseInput('Institución *', 'text', 'pep_institute', REQUIRED_RULE, {})
                ) : (
                  <div />
                )}
                {isPep === 'Si' ? (
                  buildBaseInput('Fecha de Ingreso *', 'date', 'pep_since', REQUIRED_RULE, {})
                ) : (
                  <div />
                )}
              </div>
            </div>
            <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
              <SolidButton color={BUTTON_COLORS.BLUE} className="w-auto px-12" isLoading={loading}>
                Guardar cambios
              </SolidButton>
            </div>
          </form>
        </div>
      </div>
    </PrivateRoute>
  );
}
